





import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    defineComponent,
    watch,
} from '@vue/composition-api';

import { ExpandedEventInUrl } from '@app/enums';
import { RouteName } from '@app/router/constants/RouteName';
import { slugify } from '@app/utils/slugify';
import OfferEvent from '@core/models/offer/OfferEvent';
import { getTimeSpanForTimeFilter } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/types';
import marketsIndex from '@store/modules/ui/sportOffer/markets';
import { SportOfferDataStore } from '@store/modules/data/sportOffer/index';
import { SportOfferUIStore } from '@store/modules/ui/sportOffer/sports/sportsIndex';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { getUniqueTournamentId } from '@store/modules/ui/sportOffer/marketFilters/helpers';
import { getEventSlugFromEvent } from '@app/modules/eventDetailsPage/utils';
import { AvailableCompetitionTabs } from '@sportOfferFederation/competitions/store/ui/types';
import { simplifyTournamentName } from '@core/utils/sportsOffer/simplifyTournamentName';
import { getShellConfig } from '@src/config/shell/config';

export default defineComponent({
    setup() {
        const { route, router } = useVueRouter();
        const isEventRedirect = computed(() => {
            return route.value.name === RouteName.Sport.EventRedirect;
        });
        const isCategoryRedirect = computed(() => {
            return route.value.name === RouteName.Sport.CategoryRedirect;
        });
        const isOutrightsTab = computed(() => {
            return route.value.query?.[ExpandedEventInUrl.competitionTab]?.toString() === AvailableCompetitionTabs.Outrights;
        });
        const sportOfferModule = useVuexStore<SportOfferDataStore>('data/sportOffer');
        const fetchEvents = sportOfferModule.action('fetchEvents');
        const fetchEventsByIds = sportOfferModule.action('fetchEventsByIds');
        const fetchEventsForTournaments = sportOfferModule.action('fetchEventsForTournaments');
        const fetchOutrights = sportOfferModule.action('fetchOutrights');
        const isOfferReady = sportOfferModule.getter('isOfferReady');
        const events = sportOfferModule.getter('events');
        const sports = sportOfferModule.getter('sports');
        const categories = sportOfferModule.getter('categories');
        const tournaments = sportOfferModule.getter('tournaments');

        const sportOfferMarketsStore = useVuexStore<typeof marketsIndex>('ui/sportOffer/markets');
        const setSelectedMarketGroupId = sportOfferMarketsStore.action('setSelectedMarketGroupId');

        const sportOfferSportsStore = useVuexStore<SportOfferUIStore>('ui/sportOffer/sports');
        const currentSportId = sportOfferSportsStore.getter('currentSportId');

        watch(
            () => isOfferReady.value,
            redirectWatcher,
            {
                immediate: true,
            },
        );

        // eslint-disable-next-line sonarjs/cognitive-complexity
        async function redirectWatcher(isOfferReady: boolean): Promise<void> {
            if (!isOfferReady) {
                return;
            }

            const event = await getEvent();
            if (!event) {
                await router.value.replace(
                    {
                        name: RouteName.App.NotFound,
                    },
                );
                return;
            }

            const categoryName = categories.value[event.categoryId].urlName;

            const params: Record<string, string> = {
                sportSlug: slugify(sports.value[event.sportId].urlName),
                categorySlug: slugify(categories.value[event.categoryId].urlName),
                eventSlug: getEventSlugFromEvent(event),
                timeFilter: TimeFilterSlug.All,
            };

            if (!isCategoryRedirect.value) {
                const originalTournamentName = tournaments.value[event.tournamentId].urlName;
                const tournamentName = simplifyTournamentName(originalTournamentName, categoryName);
                params.tournamentSlug = slugify(tournamentName);
            }

            const routeName = (() => {
                if (isEventRedirect.value) {
                    return RouteName.Sport.OfferEvent;
                } else if (isCategoryRedirect.value) {
                    return RouteName.Sport.PrematchOffer;
                } else {
                    return RouteName.Sport.Competition;
                }
            })();

            const query = (() => {
                if (isEventRedirect.value) {
                    return {
                        [ExpandedEventInUrl.event]: event.id.toString(),
                        [ExpandedEventInUrl.type]: getUniqueTournamentId(
                            'offer',
                            event.tournamentId,
                            event.isOngoing(),
                        ),
                    };
                } else if (!isCategoryRedirect.value) {
                    const query = {
                        ...route.value.query,
                    };

                    if (getShellConfig().remoteConfig.sportsOffer.useSimplifiedTournamentName) {
                        return query;
                    } else {
                        return {
                            ...query,
                            [ExpandedEventInUrl.tournamentId]: event.tournamentId.toString(),
                        };
                    }
                }
            })();

            if (isEventRedirect.value) {
                await setSelectedMarketGroupId(null);
            }

            await router.value.replace({
                name: routeName,
                params,
                query,
            });
        }

        async function loadApplicableEvents(): Promise<unknown> {
            if (isEventRedirect.value) {
                const eventIds = [Number(route.value.params.id)];
                return fetchEventsByIds({ eventIds });
            } else if (isCategoryRedirect.value) {
                const timeSpan = getTimeSpanForTimeFilter(TimeFilter.All);
                return fetchEvents({
                    timeSpan,
                    sportId: currentSportId.value,
                });
            } else { // tournament redirect
                const tournamentIds = route.value.params.id.split(',').map((it) => Number(it));
                const awaiters: Array<() => Promise<unknown>> = [() => fetchEventsForTournaments(tournamentIds)];

                // If this is outrights only, which happens on rare competitions, we might not even get any
                // data from the regular offer, which is why if it's an Outrights tab, we really should pre-load
                // outrights, too, otherwise customers might run into a 404 for no good reason.
                // However, only if it's the outrights tab, since we might require that info, otherwise it will
                // load in good time if it exists and there's no rush.
                if (isOutrightsTab.value) {
                    awaiters.push(() => fetchOutrights());
                }

                const results = await Promise.all(awaiters.map((fn) => fn()));
                return results.flat();
            }
        }

        async function getEvent(): Promise<OfferEvent | undefined> {
            // Maybe we already have the event available ...
            let event = findEvent();
            if (!event) {
                // ... if not, try to find the event by loading the correct segment of the offer
                await loadApplicableEvents();
                event = findEvent();
            }
            return event;
        }

        function findEvent(): OfferEvent | undefined {
            // eslint-disable-next-line no-nested-ternary
            const property: keyof OfferEvent = isEventRedirect.value
                ? 'id'
                : isCategoryRedirect.value
                    ? 'categoryId'
                    : 'tournamentId';

            if (route.value.params.id.includes(',')) {
                const ids = new Set(route.value.params.id.split(',').map((part) => parseInt(part, 10)));
                // Look for active betting value first, since we have a multi-choice, _only_ fall back
                // to inactive offer if there is no active one to work with
                return events.value.find((match) => match.oddCount! > 0 && ids.has(match[property]))
                    ?? events.value.find((match) => ids.has(match[property]));
            } else {
                const id = parseInt(route.value.params.id, 10);
                return events.value.find((match) => match[property] === id);
            }
        }
    },
});
